import React from 'react';
import type { TFunction } from 'i18next';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetClientWithSymphonyCodeDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { getCountryNameUI } from '../../../utils/getCountryName';
interface ClientSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  withMerging?: boolean;
  clientHeadId?: string;
  refresh?: boolean;
  enableMultiSelect?: boolean;
  selectedKeys?: string[];
}

export const clientForGrid = (
  t: TFunction<'clientLng', 'clientLng'>,
  tk: TKeys<'clientLng'>,
  client: GetClientWithSymphonyCodeDto & { headId?: string },
) => {
  return {
    key: client.id,
    values: {
      'client.systemNumber': client.systemNumber,
      'client.dealGroupNumber': client.dealGroupNumber,
      'client.name': client.name,
      'client.nip': client.nip,
      'client.pesel': client.pesel,
      'client.place': client.place,
      'client.street': client.street,
      'client.numbers': client.numbers,
      'client.flat': client.flat,
      'symphonyClient.code': client.symphonyCode, // used in ClientWithSymphonyCodeSearch
      client_overdue_payment_reminder_yes_no: client.overduePaymentReminder
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
      client_payment_request_yes_no: client.paymentRequest
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
      client_payment_request_krd_yes_no: client.paymentRequestKRD
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
      client_payment_request_block_krd_yes_no: client.paymentRequestBlockKRD
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
      client_interest_yes_no: client.interest
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
      has_active_purchaser: client.hasActivePurchaser ? (
        <>
          <Icon.Cart3 className="me-1 icon-green" />
          {t(tk.common.yesUpperCase)}
        </>
      ) : (
        t(tk.common.noUpperCase)
      ),
      has_active_position: client.hasActivePosition ? (
        <>
          <Icon.Briefcase className="me-1 icon-blue" />
          {t(tk.common.yesUpperCase)}
        </>
      ) : (
        t(tk.common.noUpperCase)
      ),
      country_label: getCountryNameUI(client.country),
    },
    isInactive: !client.active,
  };
};

export const ClientSearch: React.FC<ClientSearchProps> = props => {
  const { t, tk } = useTranslation('clientLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'client.systemNumber' },
    {
      header: t(tk.client.dealGroupNumber),
      property: 'client.dealGroupNumber',
    },
    { header: t(tk.common.name), property: 'client.name' },
    { header: t(tk.client.nip), property: 'client.nip' },
    { header: t(tk.client.pesel), property: 'client.pesel' },
    { header: t(tk.client.country), property: 'country_label' },
    { header: t(tk.client.place), property: 'client.place' },
    { header: t(tk.client.street), property: 'client.street' },
    { header: t(tk.client.numbers), property: 'client.numbers' },
    { header: t(tk.client.flat), property: 'client.flat' },
    {
      header: t(tk.client.hasActivePurchaser),
      property: 'has_active_purchaser',
    },
    {
      header: t(tk.client.hasActivePosition),
      property: 'has_active_position',
    },
    {
      header: t(tk.client.overduePaymentReminder),
      property: 'client_overdue_payment_reminder_yes_no',
    },
    {
      header: t(tk.client.paymentRequest),
      property: 'client_payment_request_yes_no',
    },
    {
      header: t(tk.client.paymentRequestKRD),
      property: 'client_payment_request_krd_yes_no',
    },
    {
      header: t(tk.client.paymentRequestBlockKRD),
      property: 'client_payment_request_block_krd_yes_no',
    },
    {
      header: t(tk.client.interest),
      property: 'client_interest_yes_no',
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="client.name"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.client.noClients)}
      additionalId={props.clientHeadId}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={true}
      withFilterInColumns={true}
      showExport={true}
      enableMultiSelect={props.enableMultiSelect}
      selectedKeys={props.selectedKeys}
    />
  );
};
